import React, { useState, useEffect } from 'react';
import defaultTo from 'lodash/defaultTo';
import { Offering, PricingType, Promotion, EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { Field } from '@atlaskit/form';
import Button from '@atlaskit/button';
import InfoIcon from '@atlaskit/icon/glyph/info';
import SectionMessage from '@atlaskit/section-message';
import Tooltip from '@atlaskit/tooltip';
import {
  getPromotionTitle,
  isDiscretionaryDiscountPromotion,
  renderPromotionValue,
  SelectPromotionSelectItem,
  InfoPanel,
  withApollo,
  useFeatureToggle,
} from '@css-xp/base';
import { PromotionItemDetail, SelectPromotionFormFieldV2 } from '@css-xp/base/forms';
import { getAppropriatePromotions } from '@atlassian/temp-helper';
import classNames from 'classnames';
import map from 'lodash/fp/map';
import get from 'lodash/get';
import intersectionBy from 'lodash/intersectionBy';
import { FormData } from './manage-entitlement';
import { ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE } from '@atlassian/cssxp-utils-generic';
import { MARKETPLACE_PROMO_CODE } from '@atlassian/cssxp-utils-entitlement';

interface Props {
  entitlement: EntitlementDetails;
  formState: FormData;
  availableOfferings: Offering[];
  availablePromotions: Promotion[];
  onCustomPromotionValueChange?: (item: PromotionItemDetail) => void;
  isUpgradeTrialScenario: boolean;
  isOfferingChanged?: boolean;
}

interface PromotionListProps {
  promotions: Promotion[];
  onChange: React.Dispatch<React.SetStateAction<Promotion[]>>;
  formState: FormData;
  availablePromotionTypes: string[];
  activePromotionsToRemove?: Promotion[] | [];
  isOnUpgradeTrial: boolean;
  setAvailablePromotionTypes: (promotionType: string[]) => void;
}

type SelectedPromotion = {
  promotionId: string;
  index: string;
};

export const filterPromotionItem = (formState: FormData) => {
  const selectedPromo: SelectPromotionSelectItem[] = [];
  for (let i = 0; ; i++) {
    const promotionItem = get(formState, `promotionId-${i}`, '');
    if (promotionItem) {
      if (isDiscretionaryDiscountPromotion(promotionItem)) {
        const customPromotion = get(formState, `customPromotionId-${i}`, '');
        // adding customFormField to that we can easily re-populate the fields on coming back to
        // manage promotion step
        customPromotion.promotion.customFormField = {
          iterations: defaultTo(get(formState, 'customIterations'), [])[i],
          value: defaultTo(get(formState, 'customValue'), [])[i],
          reason: defaultTo(get(formState, 'customReason'), [])[i],
          duration: defaultTo(get(formState, 'customDuration'), [])[i],
        };
        selectedPromo.push(customPromotion);
      } else {
        selectedPromo.push(promotionItem);
      }
    } else break;
  }
  return selectedPromo;
};

const selector = (promotion: Promotion) => ({ promotion });
const formStateSelector = (promotionItem: SelectPromotionSelectItem) => `${promotionItem?.promotion?.id}`;

const PromotionList = ({
  promotions,
  onChange,
  formState,
  availablePromotionTypes,
  setAvailablePromotionTypes,
  isOnUpgradeTrial,
}: PromotionListProps) => {
  const [promotionsToRemove, setPromotionsToRemove] = React.useState<string[]>(
    map(formStateSelector, formState?.promotionsToRemove)
  );

  React.useEffect(() => {
    onChange(
      map(
        selector,
        intersectionBy(
          promotions,
          promotionsToRemove.map((id) => ({ id })),
          'id'
        )
      ) as Promotion[]
    );
  }, [promotionsToRemove]);

  const onRemovePromotion = (id: string, promotionType: string) => {
    const index = promotionsToRemove.indexOf(id);
    if (index >= 0) {
      promotionsToRemove.splice(index, 1);
      setPromotionsToRemove([...promotionsToRemove]);
      setAvailablePromotionTypes([...availablePromotionTypes, promotionType]);
    } else {
      setPromotionsToRemove([...promotionsToRemove, id]);
      const promotionsTypeToRemove = availablePromotionTypes.filter((type) => promotionType !== type);
      setAvailablePromotionTypes(promotionsTypeToRemove);
    }
  };

  return (
    <>
      {defaultTo(promotions, []).map((item, index) => {
        const shouldRemove = promotionsToRemove.indexOf(defaultTo(item?.id, '')) < 0;
        return (
          <div
            data-testid={!shouldRemove ? 'removed-tile' : 'added-tile'}
            key={index}
            className="promotion-view list-item"
            style={{ marginBottom: '1%' }}
          >
            <strong className={classNames({ 'remove-on': !shouldRemove })}>
              {getPromotionTitle(item, { handleEmptyTitle: true })}
            </strong>
            <div className="remove-promotion-link-container">
              <p className={classNames({ 'remove-on': !shouldRemove })}>
                {renderPromotionValue(item, { showDuration: true, showReason: true })}
              </p>

              <div className="remove-promotion-button" style={{ textAlign: 'right' }}>
                <Button
                  isDisabled={isOnUpgradeTrial}
                  className={classNames({ remove: shouldRemove })}
                  appearance="link"
                  testId="remove-button"
                  aria-label="Remove promotion"
                  onClick={() => onRemovePromotion(defaultTo(item?.id, ''), defaultTo(item?.title, ''))}
                >
                  {shouldRemove ? 'Remove' : 'Undo removal'}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

const ScheduledPromotionList = ({
  promotions,
  onChange,
  formState,
  activePromotionsToRemove,
  availablePromotionTypes,
  setAvailablePromotionTypes,
  isOnUpgradeTrial,
}: PromotionListProps) => {
  const [schedulePromotionsToRemove, setSchedulePromotionsToRemove] = React.useState<string[]>(
    map(formStateSelector, formState?.schedulePromotionsToRemove)
  );
  React.useEffect(() => {
    onChange(
      map(
        selector,
        intersectionBy(
          promotions,
          schedulePromotionsToRemove.map((id) => ({ id })),
          'id'
        )
      ) as Promotion[]
    );
  }, [schedulePromotionsToRemove]);

  const onRemoveSchedulePromotion = (id: string, promotionType: string) => {
    const index = schedulePromotionsToRemove.indexOf(id);
    if (index >= 0) {
      schedulePromotionsToRemove.splice(index, 1);
      setSchedulePromotionsToRemove([...schedulePromotionsToRemove]);
      setAvailablePromotionTypes([...availablePromotionTypes, promotionType]);
    } else {
      setSchedulePromotionsToRemove([...schedulePromotionsToRemove, id]);
      const promotionsTypeToRemove = availablePromotionTypes.filter((type) => promotionType !== type);
      setAvailablePromotionTypes(promotionsTypeToRemove);
    }
  };

  return (
    <>
      {defaultTo(promotions, []).map((item, index) => {
        let shouldRemove = schedulePromotionsToRemove.indexOf(defaultTo(item?.id, '')) < 0;
        if (activePromotionsToRemove && activePromotionsToRemove?.length > 0) {
          shouldRemove = false;
        }
        return (
          <div
            data-testid={!shouldRemove ? 'schedule-removed-tile' : 'schedule-added-tile'}
            key={index}
            className="promotion-view list-item"
            style={{ marginBottom: '1%' }}
          >
            <strong className={classNames({ 'remove-on': !shouldRemove })}>
              {getPromotionTitle(item, { handleEmptyTitle: true })}
            </strong>
            <p className={classNames({ 'remove-on': !shouldRemove })}>
              {renderPromotionValue(item, { showDuration: true, showReason: true })}
            </p>
            {activePromotionsToRemove?.length === 0 && (
              <div className="action-row">
                <Button
                  isDisabled={isOnUpgradeTrial}
                  className={classNames({ remove: shouldRemove })}
                  appearance="link"
                  testId="schedule-remove-button"
                  aria-label="Remove promotion"
                  onClick={() => onRemoveSchedulePromotion(defaultTo(item?.id, ''), defaultTo(item?.type, ''))}
                >
                  {shouldRemove ? 'Remove' : 'Undo removal'}
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

const filterValidPromotion = (selectedPromo: SelectPromotionSelectItem) => {
  const promotion = selectedPromo?.promotion;
  return !!promotion?.id;
};

const filterSelectedOffering = (formState: FormData, offering: Offering) => {
  return offering.id === formState?.newPlan?.value;
};

export const ManagePromotionComponent = ({
  entitlement,
  formState,
  availableOfferings,
  availablePromotions,
  onCustomPromotionValueChange,
  isUpgradeTrialScenario,
  isOfferingChanged,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const activePromotions = defaultTo(entitlement?.promotions, []).filter((promotion) => !!promotion) as Promotion[];

  const scheduledPromotions = entitlement?.scheduledChanges?.promotions?.map((promotion, index) => {
    return {
      ...promotion,
      index: index.toString(),
    };
  }) as Promotion[];

  const [activeEntitlementPromotionsToRemove, setActiveEntitlementPromotionsToRemove] = useState<SelectedPromotion[]>(
    []
  );
  const [scheduledPromotionsToRemove, setScheduledPromotionsToRemove] = useState<SelectedPromotion[]>([]);
  // add all the scheduled promotions for removal when removing an active promotion
  useEffect(() => {
    if (activeEntitlementPromotionsToRemove.length) {
      const allScheduledPromotions = defaultTo(
        appropriateScheduledPromotions?.map((promotion: Promotion, index: number) => {
          return { promotionId: promotion.id as string, index: index.toString() };
        }),
        []
      );
      setScheduledPromotionsToRemove(allScheduledPromotions);
    }
  }, [activeEntitlementPromotionsToRemove]);

  const hasScheduledPromotions: boolean = (scheduledPromotions ?? []).length > 0;
  const formRemovePromotions = defaultTo(formState?.promotionsToRemove, []).map(
    (item) => item?.promotion
  ) as Promotion[];
  const [promotionsToRemove, setPromotionsToRemove] = React.useState<Promotion[]>(formRemovePromotions);

  const formRemoveSchedulePromotions = defaultTo(formState?.promotionsToRemove, []).map(
    (item) => item?.promotion
  ) as Promotion[];
  const [schedulePromotionsToRemove, setSchedulePromotionsToRemove] = React.useState<Promotion[]>(
    formRemoveSchedulePromotions
  );

  const formAddPromotions = defaultTo(filterPromotionItem(formState), [])
    .filter(filterValidPromotion)
    .map((item) => item?.promotion) as Promotion[];

  const appropriateAvailablePromotions = getAppropriatePromotions(
    availablePromotions,
    formState?.billingFrequency ?? entitlement?.billingFrequency
  );
  const appropriateScheduledPromotions = getAppropriatePromotions(
    scheduledPromotions,
    formState?.billingFrequency ?? entitlement?.billingFrequency
  );

  const isMarketPlacePromocodeCurrentPromotionApplied = (activePromotions || []).find(
    (item: any) => item.type === 'PROMO_CODE'
  );

  const isMarketPlacePromocodeScheduledPromotionApplied = (appropriateScheduledPromotions || []).find(
    (item: any) => item.type === 'PROMO_CODE'
  );

  if (
    isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE) &&
    entitlement?.isApp &&
    !isMarketPlacePromocodeScheduledPromotionApplied &&
    !isMarketPlacePromocodeCurrentPromotionApplied
  ) {
    appropriateAvailablePromotions.push({
      type: MARKETPLACE_PROMO_CODE,
      title: 'Promo code',
      status: 'ACTIVE',
    });
  }

  let isFreeProduct = false;
  const selectedOffering = defaultTo(availableOfferings, []).filter(filterSelectedOffering.bind(this, formState));
  isFreeProduct = defaultTo(selectedOffering?.[0]?.pricingType, '') === PricingType.FREE;

  const [availablePromotionTypes, setAvailablePromotionTypes] = useState<string[]>(
    activePromotions.concat(scheduledPromotions || [])?.map((item: any) => item.type) ?? []
  );

  return (
    <div className="promotion-view">
      {activePromotions && activePromotions?.length > 0 && (
        <Field defaultValue={promotionsToRemove} name="promotionsToRemove" id="promotionsToRemove" label="">
          {({ fieldProps }) => (
            <>
              <h3>
                <span className="mr-4 active-promotion-heading">Current discounts</span>
                <Tooltip content={<>Includes promotions that will be applied to the next bill.</>} tag="span">
                  <InfoIcon size="small" label="active-promotion-info" />
                </Tooltip>
              </h3>
              <div data-testid="currentPromotionBox">
                <PromotionList
                  {...fieldProps}
                  onChange={setPromotionsToRemove}
                  promotions={activePromotions}
                  formState={formState}
                  setAvailablePromotionTypes={setAvailablePromotionTypes}
                  availablePromotionTypes={availablePromotionTypes}
                  isOnUpgradeTrial={isUpgradeTrialScenario}
                />
              </div>
            </>
          )}
        </Field>
      )}

      {hasScheduledPromotions && !isUpgradeTrialScenario && !isOfferingChanged && (
        <Field
          defaultValue={schedulePromotionsToRemove}
          name="schedulePromotionsToRemove"
          id="schedulePromotionsToRemove"
          label=""
        >
          {({ fieldProps }) => (
            <>
              <h3>
                <span className="mr-4 active-promotion-heading">Promotions to be applied to next bill</span>
                <Tooltip content={<>Includes promotions that will be applied to the next bill.</>} tag="span">
                  <InfoIcon size="small" label="active-promotion-info" />
                </Tooltip>
              </h3>
              {promotionsToRemove.length > 0 && (
                <div style={{ marginBottom: '1%' }}>
                  <InfoPanel
                    type="warning"
                    message="Removal of any active promotion will also remove all scheduled promotions."
                  />
                </div>
              )}
              <div data-testid="schedulePromotionBox">
                <ScheduledPromotionList
                  {...fieldProps}
                  onChange={setSchedulePromotionsToRemove}
                  promotions={scheduledPromotions}
                  activePromotionsToRemove={promotionsToRemove}
                  formState={formState}
                  setAvailablePromotionTypes={setAvailablePromotionTypes}
                  availablePromotionTypes={availablePromotionTypes}
                  isOnUpgradeTrial={isUpgradeTrialScenario}
                />
              </div>
            </>
          )}
        </Field>
      )}
      <h2>Add discounts</h2>
      {isFreeProduct ? (
        <SectionMessage appearance="warning">
          <strong>No discounts can be added to a Free plan</strong>
        </SectionMessage>
      ) : (
        <SelectPromotionFormFieldV2
          availablePromotions={appropriateAvailablePromotions}
          promotions={formAddPromotions}
          onCustomValueChange={onCustomPromotionValueChange}
          fieldLabel={'Discount'}
          availablePromotionTypes={availablePromotionTypes}
          productId={entitlement?.productId}
          isDisabled={isUpgradeTrialScenario}
        />
      )}
    </div>
  );
};

export const ManagePromotion = withApollo(ManagePromotionComponent);
